import { Edit02Icon, RedoIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useAuthContext } from "providers/Auth";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";
import {
  Show,
  Button,
  ButtonVariant,
  ButtonSize,
  Hide
} from "shared/components";
import { IncomeStatus, IncomeType, ApplicationStatus } from "shared/types";
import { EditIncomeModalData } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import { getIncomeActualNetAndGrossDollars } from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils";

interface Props {
  setShowEditIncomeModal: (data: false | EditIncomeModalData) => void;
  setShowReverifyIncomeModal: (data: number | false) => void;
  income: CombinedIncome;
  applicationStatus: ApplicationStatus;
  applicationId?: string | number;
  currentItem?: VerifiedItem | VerifiedItem[] | undefined;
  isManualApplicant?: boolean;
}

export const IncomeActionButtons = ({
  setShowEditIncomeModal,
  setShowReverifyIncomeModal,
  income,
  applicationStatus,
  applicationId,
  currentItem,
  isManualApplicant
}: Props) => {
  const { permissions } = useAuthContext();
  const { actualVerifiedNetMonthlyDollars, actualVerifiedGrossMonthlyDollars } =
    getIncomeActualNetAndGrossDollars(income, currentItem);

  const isCurrentItemArchived = Array.isArray(currentItem)
    ? currentItem.every((item) => item.is_archived)
    : (currentItem?.is_archived ?? false);

  return (
    <Show
      if={
        ![IncomeStatus.Processing, IncomeStatus.Pending].includes(income.status)
      }
    >
      <div className="flex justify-between gap-1 w-full">
        <div className="space-y-1">
          <p className="text-content-1000 font-bold text-lg">
            Income source summary
          </p>
          <p className="text-content-300 font-medium text-sm">
            You can edit or re-verify the income source details
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            HugeIcon={Edit02Icon}
            disabled={!permissions.canEditIncome}
            tooltip={
              !permissions.canEditIncome
                ? "You don't have permission for this action"
                : undefined
            }
            onClick={() => {
              setShowEditIncomeModal({
                grossMonthlyIncomeDollars: actualVerifiedGrossMonthlyDollars,
                netMonthlyIncomeDollars: actualVerifiedNetMonthlyDollars,
                hasNetMonthlyIncome: income.type !== "offer_letter",
                incomeId: income.id,
                isTotal: false
              });
            }}
          >
            Edit
          </Button>
          <Hide
            if={
              income.type === IncomeType.OfferLetter ||
              isCurrentItemArchived ||
              isManualApplicant
            }
          >
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              HugeIcon={RedoIcon}
              disabled={applicationStatus === ApplicationStatus.OnHold}
              tooltip={
                applicationStatus === ApplicationStatus.OnHold ? (
                  <ResumeTooltip applicationId={Number(applicationId || 0)} />
                ) : undefined
              }
              tooltipDelay={250}
              onClick={() => setShowReverifyIncomeModal(income.id)}
            >
              Re-verify
            </Button>
          </Hide>
        </div>
      </div>
      <hr className="bg-alpha-40 my-5" />
    </Show>
  );
};
